import React from 'react';
import {
  derivePresenceStatus,
  PRESENCE_ACTIVITY_THROTTLE_MS,
  PRESENCE_EVALUATION_INTERVAL_MS,
  PRESENCE_KEEPALIVE_MS,
  updateUserPresenceForProfile,
  type UserPresenceStatus,
} from '../data/presence';
import useSessionStore from '../state/session';

const APP_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'mousemove', 'scroll', 'touchstart'] as const;

async function writePresence(profileId: string, status: UserPresenceStatus): Promise<void> {
  const error = await updateUserPresenceForProfile(profileId, status);
  if (error) {
    console.warn('[presence] Failed to update app presence:', error);
  }
}

export function useAppPresence(enabled = true): void {
  const session = useSessionStore((state) => state.session);
  const profileId = useSessionStore((state) => state.profileId);
  const sessionReady = useSessionStore((state) => state.sessionReady);
  const hasSession = Boolean(session?.user?.id);
  const currentStatusRef = React.useRef<UserPresenceStatus | null>(null);
  const lastActivityAtRef = React.useRef<number>(Date.now());
  const lastWriteAtRef = React.useRef<number>(0);
  const lastActivityDispatchAtRef = React.useRef<number>(0);

  const publish = React.useCallback(
    (status: UserPresenceStatus, force = false) => {
      if (!enabled || !hasSession || !sessionReady || !profileId) return;
      if (!force && currentStatusRef.current === status) return;
      currentStatusRef.current = status;
      lastWriteAtRef.current = Date.now();
      void writePresence(profileId, status);
    },
    [enabled, hasSession, profileId, sessionReady],
  );

  const evaluate = React.useCallback((): Exclude<UserPresenceStatus, 'offline'> => {
    return derivePresenceStatus(Date.now() - lastActivityAtRef.current);
  }, []);

  const markActive = React.useCallback(() => {
    const now = Date.now();
    lastActivityAtRef.current = now;
    if (now - lastActivityDispatchAtRef.current < PRESENCE_ACTIVITY_THROTTLE_MS) {
      return;
    }
    lastActivityDispatchAtRef.current = now;
    publish('online');
  }, [publish]);

  React.useEffect(() => {
    if (!enabled || !hasSession || !sessionReady || !profileId) {
      currentStatusRef.current = null;
      lastWriteAtRef.current = 0;
      return undefined;
    }

    lastActivityAtRef.current = Date.now();
    lastActivityDispatchAtRef.current = 0;
    publish('online', true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markActive();
        return;
      }
      const nextStatus = evaluate();
      publish(nextStatus === 'online' ? 'idle' : nextStatus, true);
    };

    APP_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, markActive, { passive: true });
    });
    window.addEventListener('focus', markActive);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const interval = window.setInterval(() => {
      const nextStatus = evaluate();
      const now = Date.now();
      if (nextStatus !== currentStatusRef.current) {
        publish(nextStatus, true);
        return;
      }
      if (now - lastWriteAtRef.current >= PRESENCE_KEEPALIVE_MS) {
        publish(nextStatus, true);
      }
    }, PRESENCE_EVALUATION_INTERVAL_MS);

    return () => {
      APP_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, markActive);
      });
      window.removeEventListener('focus', markActive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(interval);
      if (currentStatusRef.current !== 'offline') {
        currentStatusRef.current = 'offline';
        void writePresence(profileId, 'offline');
      }
    };
  }, [enabled, evaluate, hasSession, markActive, profileId, publish, sessionReady]);
}
